import {
  Auth0Provider,
  AppState,
  Auth0ProviderOptions,
} from "@auth0/auth0-react";
import { defaultScope } from "constants/idp-scopes";
import React, { PropsWithChildren, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRootSelector } from "store";
import { ReAuthContext } from "context/ReAuthContext";

interface Auth0ProviderWithNavigateProps {
  children: React.ReactNode;
}

export const Auth0ProviderWithNavigate: React.FC<
  Auth0ProviderWithNavigateProps
> = ({
  children,
}: PropsWithChildren<Auth0ProviderWithNavigateProps>): JSX.Element | null => {
  const navigate = useNavigate();
  const { customerKey } = useParams();
  const { client } = useRootSelector((state) => state.config);
  const { dispatch: reAuthDispatch } = useContext(ReAuthContext);

  const onRedirectCallback = (appState?: AppState) => {
    reAuthDispatch({
      type: "SET_REAUTH",
      payload: { isReauthenticated: true },
    });
    let to = appState?.returnTo;
    if (!to && !!client && !!client.customerKey) {
      to = `/${client.customerKey}/dashboard`;
    } else {
      to = window.location.pathname;
    }

    navigate(to);
  };

  if (
    !!client &&
    !!client.idPId &&
    customerKey?.toLocaleLowerCase() === client.customerKey
  ) {
    const redirectUri = new URL(
      `/${client.customerKey}`,
      window.location.toString(),
    );

    const cacheLocation = import.meta.env.REACT_APP_AUTH0_CACHE_LOCATION as
      | "memory"
      | "localstorage"
      | undefined;

    const providerConfig: Auth0ProviderOptions = {
      domain: import.meta.env.REACT_APP_AUTH0_DOMAIN!,
      clientId: client.idPId,
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      cacheLocation: cacheLocation || "localstorage",
      authorizationParams: {
        audience: import.meta.env.REACT_APP_AUTH0_AUDIENCE
          ? import.meta.env.REACT_APP_AUTH0_AUDIENCE
          : "",
        redirect_uri: redirectUri.toString(),
        scope: defaultScope,
      },
      onRedirectCallback,
    };

    return <Auth0Provider {...providerConfig}>{children}</Auth0Provider>;
  } else {
    return <>{children}</>;
  }
};
